import { useEffect, useState } from 'react';
import { fetchManualAsset } from '../services/umaService';

export interface UseManualAssetResult {
  src: string | null;
  loading: boolean;
  error: boolean;
}

/**
 * Blob-fetches a JWT-gated `/api/v1/uma/assets/...` URL (via
 * umaService.fetchManualAsset, which carries the Bearer token through
 * apiClient) and exposes the resulting object URL.
 *
 * T-117-19 mitigation: revokes the object URL on unmount AND whenever
 * `assetUrl` changes — a single chat session can render many distinct
 * screenshots over its lifetime, so per-image revoke-on-unmount replaces the
 * one-shot 60s timer used elsewhere (fileService.open) for a single download.
 */
export const useManualAsset = (
  assetUrl: string | null | undefined
): UseManualAssetResult => {
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(!!assetUrl);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    if (!assetUrl) {
      setSrc(null);
      setLoading(false);
      setError(false);
      return undefined;
    }

    let cancelled = false;
    let objectUrl: string | null = null;

    setLoading(true);
    setError(false);

    fetchManualAsset(assetUrl)
      .then((url) => {
        if (cancelled) {
          // Component unmounted / url changed before the fetch resolved —
          // revoke immediately instead of leaking it into state.
          URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url;
        setSrc(url);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [assetUrl]);

  return { src, loading, error };
};

export default useManualAsset;
